import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { connect, useSelector } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import moment from 'moment';

import { getMyDashboardData, GetMyDashboardData } from '../../lib/Api/participant/Dashboard';
import { isAfter } from '../../lib/TimeUtils';

import { EmployeePageWrapper } from '../../components/UI';
import Tabs from '../../components/UI/Tabs';

import { PageContent } from '../../Themes';
import { lockIconWithCircle } from '../../assets/icons';
import {
  StyledTrophyCabinet,
  Title,
  TitleCount,
  TitleText,
  AchievementsContainer,
  AchievementWrapper,
  Achievement,
  AchievementName,
  AchievementPoints,
  AchievementImageWrapper,
  LockIcon,
  RewardTooltip
} from './Styles';

import { getRewardTooltip } from './rewardTooltips';

import { PointsType } from '../../lib/Types';
import { ApplicationState } from '../../lib/Store';

interface TrophyCabinetProps {
  getMyDashboardData: GetMyDashboardData;
}

const TrophyCabinet: React.FC<TrophyCabinetProps> = ({ getMyDashboardData }) => {
  const tabs = ['Connection Rewards', 'Movement Rewards', 'Survey Rewards'];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const showTooltip = (event: React.MouseEvent | React.FocusEvent, text: string) => {
    if ('clientX' in event) {
      setTooltip({ text, x: event.clientX, y: event.clientY });
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ text, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
  };

  const hideTooltip = () => setTooltip(null);

  const { dashBoardData, userEvent } = useSelector(
    ({ participantDashboardState, userState }: ApplicationState) => ({
      dashBoardData: participantDashboardState.dashBoardData,
      userEvent: userState.userEvent
    })
  );

  useEffect(() => {
    hideTooltip();
  }, [activeTab]);

  useEffect(() => {
    if (!userEvent.eventId || !isAfter(moment().valueOf(), userEvent.startDate)) return;
    (async () => await getMyDashboardData())();
  }, []);

  const visibleRewards = useMemo(() => {
    if (!dashBoardData) return [];

    return dashBoardData.rewards.filter(reward => {
      if (activeTab === 'Connection Rewards') return reward.type === PointsType.Connection;
      if (activeTab === 'Movement Rewards') return reward.type === PointsType.Movement;
      return reward.type === PointsType.Survey;
    });
  }, [dashBoardData, activeTab]);

  const getRewardKey = (reward: (typeof visibleRewards)[number], index: number) =>
    `${reward.type}-${reward.rewardId ?? reward.name}-${reward.points}-${index}`;

  const summaryText = () => {
    if (!dashBoardData) return <TitleText>Loading...</TitleText>;
    const allConnection = dashBoardData.rewards.filter(
      reward => reward.type === PointsType.Connection
    );
    const allMovement = dashBoardData.rewards.filter(reward => reward.type === PointsType.Movement);
    const allSurvey = dashBoardData.rewards.filter(reward => reward.type === PointsType.Survey);
    const earnedConnection = allConnection.filter(reward => reward.earned);
    const earnedMovement = allMovement.filter(reward => reward.earned);
    const earnedSurvey = allSurvey.filter(reward => reward.earned);

    if (activeTab === 'Connection Rewards') {
      return (
        <TitleCount>
          {`${earnedConnection.length} of ${allConnection.length}`}
          <TitleText>{` Connection Reward${
            earnedConnection.length > 1 ? 's' : ''
          } Achieved`}</TitleText>
        </TitleCount>
      );
    } else if (activeTab === 'Movement Rewards') {
      return (
        <TitleCount>
          {`${earnedMovement.length} of ${allMovement.length}`}
          <TitleText>{` Movement Reward${earnedMovement.length > 1 ? 's' : ''} Achieved`}</TitleText>
        </TitleCount>
      );
    } else {
      return (
        <TitleCount>
          {`${earnedSurvey.length} of ${allSurvey.length}`}
          <TitleText>{` Survey Reward${earnedSurvey.length > 1 ? 's' : ''} Achieved`}</TitleText>
        </TitleCount>
      );
    }
  };

  return (
    <EmployeePageWrapper holdingPageTitle="Trophy Cabinet">
      <PageContent>
        <Title>Your Trophy Cabinet</Title>
                <Tabs activeTab={activeTab} tabs={tabs} setActiveTab={setActiveTab} />
        <StyledTrophyCabinet>
          {summaryText()}
          <AchievementsContainer key={activeTab}>
            {visibleRewards.map((reward, index) => {
              const tooltipText = getRewardTooltip(reward.name);
              return (
                <AchievementWrapper
                  key={getRewardKey(reward, index)}
                  tabIndex={tooltipText ? 0 : undefined}
                  onMouseEnter={tooltipText ? event => showTooltip(event, tooltipText) : undefined}
                  onMouseMove={tooltipText ? event => showTooltip(event, tooltipText) : undefined}
                  onMouseLeave={tooltipText ? hideTooltip : undefined}
                  onFocus={tooltipText ? event => showTooltip(event, tooltipText) : undefined}
                  onBlur={tooltipText ? hideTooltip : undefined}
                >
                  <AchievementImageWrapper>
                    <Achievement src={reward.image} earned={reward.earned} />
                    {!reward.earned && <LockIcon src={lockIconWithCircle} />}
                  </AchievementImageWrapper>
                  <AchievementName>{reward.name}</AchievementName>
                  <AchievementPoints>{reward.points}pts</AchievementPoints>
                </AchievementWrapper>
              );
            })}
          </AchievementsContainer>
        </StyledTrophyCabinet>
        {tooltip &&
          ReactDOM.createPortal(
            <RewardTooltip
              role="tooltip"
              style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
            >
              {tooltip.text}
            </RewardTooltip>,
            document.body
          )}
      </PageContent>
    </EmployeePageWrapper>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getMyDashboardData: getMyDashboardData as any
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(TrophyCabinet as any);
